// src/pages/Private/Teacher/TeacherCourses.js
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getMyCourses } from '../../../api/teacherApi';
import './TeacherCourses.css';

function TeacherCourses() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [view, setView] = useState('grid'); // 'grid' or 'list'

  useEffect(() => {
    loadCourses();
  }, []);

  const loadCourses = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getMyCourses();
      setCourses(data);
    } catch (error) {
      setError('Error loading courses: ' + (error.response?.data?.msg || error.message));
      console.error('Kurslar yüklenirken hata oluştu:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStudentCount = (course) => {
    return course.students?.length || 0;
  };

  const getLessonCount = (course) => {
    return course.lessons?.length || 0;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    return new Date(dateString).toLocaleDateString('tr-TR');
  };

  if (loading) {
    return (
      <div className="teacher-courses-loading">
        <div className="loading-spinner">📚</div>
        <p>Loading courses...</p>
      </div>
    );
  }

  return (
    <div className="teacher-courses">
      {/* Header */}
      <div className="courses-header">
        <div className="header-content">
          <h1>📚 My Courses</h1>
          <p>View and manage all your courses</p>
        </div>
        <div className="header-actions">
          <button onClick={loadCourses} className="refresh-btn">
            <span className="refresh-icon">🔄</span>
            Refresh
          </button>
          <Link to="/teacher/create-course" className="create-btn">
            ➕ New Course
          </Link>
        </div>
      </div>

      {error && (
        <div className="courses-error-banner">
          <span>⚠️</span>
          <span>{error}</span>
          <button onClick={() => setError('')} className="close-btn">×</button>
        </div>
      )}

      {/* View Toggle */}
      <div className="view-controls">
        <div className="view-toggle">
          <button 
            className={view === 'grid' ? 'active' : ''}
            onClick={() => setView('grid')}
          >
            ⬜ Grid
          </button>
          <button 
            className={view === 'list' ? 'active' : ''}
            onClick={() => setView('list')}
          >
            ☰ List
          </button>
        </div>
        <div className="courses-stats">
          <span className="stat">{courses.length} courses</span>
          <span className="stat">{courses.reduce((sum, course) => sum + getStudentCount(course), 0)} students</span>
        </div>
      </div>

      {/* Courses Grid/List */}
      {courses.length === 0 ? (
        <div className="empty-courses">
          <div className="empty-icon">📚</div>
          <h3>You don't have any courses yet</h3>
          <p>Start by creating your first course</p>
          <Link to="/teacher/create-course" className="create-course-btn">
            ➕ Create Course
          </Link>
        </div>
      ) : view === 'grid' ? (
        <div className="courses-grid">
          {courses.map(course => (
            <div key={course._id} className="course-card">
              <div className="course-image">
                {course.imageUrl ? (
                  <img src={course.imageUrl} alt={course.title} />
                ) : (
                  <div className="course-image-placeholder">
                    📚
                  </div>
                )}
                {course.category && (
                  <div className="course-badge">{course.category}</div>
                )}
              </div>
              
              <div className="course-content">
                <h3 className="course-title">{course.title}</h3>
                <p className="course-description">{course.description}</p>
                
                <div className="course-meta">
                  <div className="meta-item">
                    <span className="meta-icon">👨‍🎓</span>
                    <span>{getStudentCount(course)} students</span>
                  </div>
                  <div className="meta-item">
                    <span className="meta-icon">📖</span>
                    <span>{getLessonCount(course)} lessons</span>
                  </div>
                  <div className="meta-item">
                    <span className="meta-icon">📅</span>
                    <span>{formatDate(course.createdAt)}</span>
                  </div>
                </div>

                <div className="course-actions">
                  <Link 
                    to={`/teacher/courses/${course._id}/modules`} 
                    className="btn-primary"
                  >
                    📋 Manage Modules
                  </Link>
                  <button 
                    className="btn-secondary"
                    onClick={() => setSelectedCourse(selectedCourse?._id === course._id ? null : course)}
                  >
                    {selectedCourse?._id === course._id ? '▲ Hide Details' : '▼ Show Details'}
                  </button>
                </div>

                {selectedCourse?._id === course._id && (
                  <div className="course-details">
                    <div className="detail-section">
                      <h4>Course Information</h4>
                      <p><strong>Category:</strong> {course.category || 'Not specified'}</p>
                      <p><strong>Zoom Link:</strong> 
                        {course.zoomLink ? (
                          <a href={course.zoomLink} target="_blank" rel="noopener noreferrer"> Join Class</a>
                        ) : (
                          ' Not specified'
                        )}
                      </p>
                      <p><strong>Created:</strong> {formatDate(course.createdAt)}</p>
                    </div>
                    
                    {course.students && course.students.length > 0 && (
                      <div className="detail-section">
                        <h4>Students ({course.students.length})</h4>
                        <div className="students-list">
                          {course.students.map((student, index) => (
                            <span key={index} className="student-tag">
                              {student.name || student.email}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="courses-list">
          <div className="list-header">
            <span>Course Name</span>
            <span>Students</span>
            <span>Lessons</span>
            <span>Category</span>
            <span>Actions</span>
          </div>
          {courses.map(course => (
            <div key={course._id} className="list-item">
              <div className="course-info">
                <h4>{course.title}</h4>
                <p>{course.description}</p>
              </div>
              <span className="student-count">{getStudentCount(course)}</span>
              <span className="lesson-count">{getLessonCount(course)}</span>
              <span className="category">{course.category || '-'}</span>
              <div className="action-buttons">
                <Link to={`/teacher/courses/${course._id}/modules`} className="btn-small" title="Manage Modules">
                  📋
                </Link>
                {/* Add Lesson button removed - now added through modules */}
                <Link to={`/teacher/add-student?course=${course._id}`} className="btn-small" title="Add Student">
                  👨‍🎓
                </Link>
                <button className="btn-small" title="Edit">
                  ✏️
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default TeacherCourses;